import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { sendPasswordResetAction, loadingToggleAction, socialLoginAction } from '../../store/actions/AuthActions';
import { Mail, ArrowLeft, AlertCircle, CheckCircle, Info } from 'lucide-react';
import useWindowSize, { isMobile } from '../../hooks/useWindowSize';
import { authField, authButton, authLink, authCss, GOOGLE_ICON_COLOR } from './authTheme';

// image
import loginbg from "../../images/bg-login.jpg";

const styles = {
  loginMainPage: {
    minHeight: '100vh',
    display: 'flex',
    backgroundImage: `url(${loginbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  loginWrapper: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center'
  },
  authCard: {
    background: 'rgba(255, 255, 255, 0.98)',
    borderRadius: '16px',
    padding: '3rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '450px',
    border: '1px solid #e5e7eb',
    backdropFilter: 'blur(10px)'
  },
  formGroup: {
    marginBottom: '1.5rem',
    position: 'relative'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  formInput: authField.input,
  inputIcon: authField.icon,
  submitButton: authButton.primary,
  googleButton: authButton.social,
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '2rem',
    transition: 'color 0.2s ease'
  },
  alertMessage: {
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.875rem'
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  },
  successAlert: {
    backgroundColor: 'var(--rgba-primary-1)',
    color: 'var(--primary)',
    border: '1px solid var(--rgba-primary-3)'
  },
  infoAlert: {
    backgroundColor: 'var(--rgba-primary-1)',
    color: 'var(--primary-dark)',
    border: '1px solid var(--rgba-primary-3)'
  },
  warningAlert: {
    backgroundColor: '#fffbeb',
    color: '#d97706',
    border: '1px solid #fed7aa'
  },
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Mobile-specific style overrides (applied when screen ≤ 768px)
const mobileStyles = {
  loginWrapper: {
    padding: '1rem'
  },
  authCard: {
    padding: '2rem 1.5rem',
    boxShadow: 'none',
    borderRadius: '8px'
  },
  formInput: {
    fontSize: '16px' // Prevents zoom on iOS
  }
};

// Helper function to merge mobile styles
const getResponsiveStyle = (baseStyle, mobileStyle, isMobile) => {
  return isMobile && mobileStyle ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

function ForgotPassword(props) {
  // Mobile responsiveness
  const { width } = useWindowSize();
  const mobile = isMobile(width);
  
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountInfo, setAccountInfo] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();

  // Security: Input sanitization
  const sanitizeInput = (input) => {
    return input.trim().substring(0, 254); // RFC 5321 email limit
  };

  // Security: Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setAccountInfo(null);

    const sanitizedEmail = sanitizeInput(email);

    // Enhanced validation
    if (!sanitizedEmail) {
      setError('E-postadress krävs');
      return;
    }

    if (!isValidEmail(sanitizedEmail)) {
      setError('Ogiltig e-postadress');
      return;
    }

    setIsLoading(true);
    dispatch(loadingToggleAction(true));

    try {
      const result = await dispatch(sendPasswordResetAction(sanitizedEmail));
      if (result?.success === false) setError(result.message);
      else setIsSubmitted(true);
    } catch {
      setError('Kunde inte begära återställning. Försök igen om en stund.');
    } finally {
      setIsLoading(false);
      dispatch(loadingToggleAction(false));
    }
  }

  function handleGoogleSignIn() {
    setIsLoading(true);
    dispatch(loadingToggleAction(true));
    dispatch(socialLoginAction('google', history, '/'));
  }

  React.useEffect(() => {
    if (!props.showLoading) {
      setIsLoading(false);
    }
  }, [props.showLoading]);

  // Success state
  if (isSubmitted) {
    return (
      <>
        <style>{authCss}</style>

        <div style={styles.loginMainPage} data-testid="forgot-password-success-page">
          <div style={getResponsiveStyle(styles.loginWrapper, mobileStyles.loginWrapper, mobile)}>
            <div style={getResponsiveStyle(styles.authCard, mobileStyles.authCard, mobile)} data-testid="success-card">
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: 'var(--primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }} data-testid="success-icon">
                  <CheckCircle size={32} color="white" />
                </div>

                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}
                    data-testid="success-title">
                  Kontrollera din e-post
                </h2>

                <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.6 }}
                   data-testid="success-message">
                  Din begäran har tagits emot. Om <strong>{email}</strong> kan användas för återställning får du en länk via e-post.
                  Använder du Google kan du fortsätta med Google på inloggningssidan.
                </p>

                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '2rem' }}
                   data-testid="success-additional-info">
                  Ser du inte e-posten? Kontrollera din skräppost eller försök igen om några minuter.
                </p>

                <Link
                  to="/login"
                  className="submit-button"
                  style={{
                    ...styles.submitButton,
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                  data-testid="back-to-login-button"
                >
                  Tillbaka till inloggning
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{authCss}</style>

      <div style={styles.loginMainPage} data-testid="forgot-password-page">
        <div style={getResponsiveStyle(styles.loginWrapper, mobileStyles.loginWrapper, mobile)}>
          <div style={getResponsiveStyle(styles.authCard, mobileStyles.authCard, mobile)} data-testid="forgot-password-card">
            <Link to="/login" className="back-button" style={styles.backButton} data-testid="back-to-login-link">
              <ArrowLeft size={16} />
              Tillbaka till inloggning
            </Link>

            <div style={{ marginBottom: '2rem', textAlign: 'center' }} data-testid="forgot-password-header">
              <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
                Glömt lösenord?
              </h2>
              <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                Ange din e-postadress så hjälper vi dig att komma in
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{ ...styles.alertMessage, ...styles.errorAlert }} data-testid="error-message" role="alert">
                <AlertCircle size={20} />
                {error}
              </div>
            )}

            {/* Google-only Account Info */}
            {accountInfo?.type === 'google-only' && (
              <div style={{ ...styles.alertMessage, ...styles.infoAlert }} data-testid="google-only-info" role="alert">
                <Info size={20} />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                    Detta konto använder Google för inloggning
                  </div>
                  <div style={{ fontSize: '0.825rem' }}>
                    Kontot <strong>{accountInfo.email}</strong> har inget lösenord. 
                    Logga in med Google istället.
                  </div>
                </div>
              </div>
            )}

            {/* No Password Method Info */}
            {accountInfo?.type === 'no-password' && (
              <div style={{ ...styles.alertMessage, ...styles.warningAlert }} data-testid="no-password-info" role="alert">
                <AlertCircle size={20} />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                    Kontot har inget lösenord
                  </div>
                  <div style={{ fontSize: '0.825rem' }}>
                    Detta konto använder andra inloggningsmetoder. 
                    Kontakta support för hjälp.
                  </div>
                </div>
              </div>
            )}

            {/* Show Google login for Google-only accounts */}
            {accountInfo?.type === 'google-only' ? (
              <button
                type="button"
                className="social-button"
                style={{ ...styles.googleButton, marginTop: '1rem' }}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                data-testid="google-signin-button"
              >
                {isLoading ? (
                  <div style={{ ...styles.loadingSpinner, borderColor: 'var(--rgba-primary-3)', borderTopColor: 'var(--primary)' }} data-testid="google-loading-spinner"></div>
                ) : (
                  <i className="fab fa-google" style={{ fontSize: '1.1rem', color: GOOGLE_ICON_COLOR }}></i>
                )}
                Fortsätt med Google
              </button>
            ) : (
              /* Regular password reset form */
              <form onSubmit={onSubmit} data-testid="forgot-password-form" role="form" noValidate>
                <div style={styles.formGroup}>
                  <label style={authField.label} htmlFor="email-input">
                    E-postadress
                  </label>
                  <div style={styles.inputWrapper}>
                    <Mail size={18} style={styles.inputIcon} />
                    <input
                      type="email"
                      id="email-input"
                      className={`form-input ${error ? 'error-input' : ''}`}
                      style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="din@epost.se"
                      disabled={isLoading}
                      maxLength="254"
                      autoComplete="email"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  style={styles.submitButton}
                  disabled={isLoading}
                  data-testid="submit-button"
                >
                  {isLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <div style={styles.loadingSpinner} data-testid="loading-spinner"></div>
                      Kontrollerar konto...
                    </div>
                  ) : (
                    'Skicka återställningslänk'
                  )}
                </button>
              </form>
            )}

            {/* Info section */}
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '2rem',
              fontSize: '0.875rem',
              color: '#64748b'
            }} data-testid="tips-section">
              <strong style={{ color: '#475569', display: 'block', marginBottom: '0.5rem' }}>
                💡 Tips
              </strong>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>Om du registrerade dig med Google första gången, använd "Fortsätt med Google"</li>
                <li>Kontrollera din skräppost om du inte ser återställnings-e-posten</li>
                <li>Återställningslänkar är giltiga i 1 timme</li>
              </ul>
            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem' }} data-testid="login-link-section">
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Kom du ihåg ditt lösenord?{' '}
                <Link
                  to="/login"
                  className="auth-link"
                  style={authLink}
                  data-testid="login-link"
                >
                  Logga in här
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    errorMessage: state.auth.errorMessage,
    showLoading: state.auth.showLoading,
  };
};

export default connect(mapStateToProps)(ForgotPassword);
